import { FC, useRef, useState, Fragment, ChangeEvent, useEffect } from "react";
import { StyleLabel } from "../style";
import { Field } from "formik";
import { StyleFormWrapper } from "../style";
import debounce from "@utils/debounce";
import {
  StyleCountryCodeDropDown,
  StylePhoneInput,
  StylePhoneInputWrapper,
  StyleSelectedCountryCode,
  StyleDropDownOptionWrapper,
  StyleDropDownOption,
  StyleDropDownOptionItems,
  StyleDropDownSearchWrapper,
  StyleDropDownSearchInput,
} from "./style";
import countries from "./countries";
import ErrorMessage from "../ErrorMessage";

interface PhoneInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  styleInput?: string;
  styleLabel?: string;
  styleWrapper?: string;
  onChange?: (value: string) => void;
  onChangeCode?: (codes: any) => void;
  readOnly?: boolean;
  disabled?: boolean;
  displayFullNumber?: boolean;
}

const defaultCodes = {
  callingCode: 977,
  code: "NP",
};

const PhoneInput: FC<PhoneInputProps> = ({
  name,
  label,
  styleLabel,
  styleWrapper,
  onChange,
  onChangeCode,
  readOnly = false,
  displayFullNumber = true,
  ...restInput
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [selectedCountryCode, setSelectedCountryCode] =
    useState<any>(defaultCodes);

  const handelChangeCode = ({ option }) => {
    const codes = {
      callingCode: parseInt(option?.value?.replace("+", "")),
      code: option?.code,
    };
    setSelectedCountryCode(codes);
    setSearch("");
    setIsOpen(false);
    onChangeCode && onChangeCode(codes);
  };

  const handleChange = ({ form, value }: any) => {
    const cleanedValue = value.replace(/\D/g, "");

    if (cleanedValue.length <= 10) {
      const formattedValue = displayFullNumber
        ? `+${selectedCountryCode.callingCode}${cleanedValue}`
        : cleanedValue;

      if (onChange) {
        onChange(formattedValue);
      } else {
        form.setFieldValue(name, formattedValue);
      }
    }
  };

  const handleSearch = debounce((value: string) => {
    setSearch(value);
  }, 600);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Field name={name}>
      {({ meta, form, field }) => {
        const hasError = meta.touched && meta.error;
        const selectCode = countries.find(
          (country) => country.code === selectedCountryCode?.code
        );

        const displayValue = field.value
          ? field.value.replace(`+${selectedCountryCode.callingCode}`, "")
          : "";

        return (
          <StyleFormWrapper ref={dropdownRef} styleWrapper={styleWrapper}>
            {!!label && (
              <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                {label}
              </StyleLabel>
            )}
            <StylePhoneInputWrapper hasError={hasError} isFocus={isFocus}>
              <StyleCountryCodeDropDown
                onClick={() => {
                  !readOnly && setIsOpen(true);
                }}
              >
                <StyleSelectedCountryCode>
                  {selectCode?.value}
                </StyleSelectedCountryCode>
              </StyleCountryCodeDropDown>
              <StylePhoneInput
                {...restInput}
                id={restInput.id || name}
                name={name}
                readOnly={readOnly}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleChange({
                    form,
                    value: event.target.value,
                  });
                }}
                type="tel"
                value={displayValue}
                pattern="[0-9]{1,10}"
                inputMode="numeric"
                min={0}
                maxLength={10}
                onFocus={() => {
                  setIsFocus(true);
                }}
                onBlur={() => {
                  setIsFocus(false);
                }}
              />
            </StylePhoneInputWrapper>
            {!!isOpen && (
              <StyleDropDownOptionWrapper>
                <StyleDropDownSearchWrapper>
                  <StyleDropDownSearchInput
                    type="text"
                    placeholder="Search"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleSearch(event.target.value)
                    }
                  />
                </StyleDropDownSearchWrapper>
                <StyleDropDownOptionItems>
                  {countries
                    ?.filter(
                      (option) =>
                        option?.code
                          .toLowerCase()
                          .includes(search.toLowerCase()) ||
                        option?.label
                          .toLowerCase()
                          .includes(search.toLowerCase())
                    )
                    .map((option) => (
                      <Fragment key={option?.code}>
                        <StyleDropDownOption
                          isActive={selectedCountryCode?.code == option?.code}
                          onClick={() => {
                            handelChangeCode({ option });
                          }}
                        >
                          {option?.label} ({option?.code})
                        </StyleDropDownOption>
                      </Fragment>
                    ))}
                </StyleDropDownOptionItems>
              </StyleDropDownOptionWrapper>
            )}
            {!!hasError && <ErrorMessage message={meta.error} />}
          </StyleFormWrapper>
        );
      }}
    </Field>
  );
};

export default PhoneInput;
